import {useEffect,useRef,useState} from 'react';
import {loadStripeTestCheckouts,loadStripeTestInvoice,type StripeTestCheckoutPage,type StripeTestInvoiceReadback} from './api';

const money = (minor:number) => new Intl.NumberFormat(undefined,{style:'currency',currency:'USD'}).format(minor/100);
const stateText:Record<StripeTestInvoiceReadback['state'],string> = {
  reserved:'Prepared locally; no payment confirmed',
  creation_unknown:'Session creation uncertain; no payment confirmed',
  open:'TEST session recorded; payment not confirmed',
  paid_held:'Payment evidence held for reconciliation; not settled',
  settled:'Settled in the local TEST ledger after reconciliation',
  expired:'Expired; not settled',
  failed:'Failed; not settled',
};

/** Read-only customer view. The browser return URL is never a payment signal. */
export function CustomerStripeTestInvoices({enabled,scope}:{enabled:boolean;scope:string}) {
  const [cursor,setCursor] = useState<string|null>(null);
  const [reload,setReload] = useState(0);
  const [shown,setShown] = useState<{scope:string;page:StripeTestCheckoutPage}|null>(null);
  const [pending,setPending] = useState(false);
  const [message,setMessage] = useState('');
  const generation = useRef(0);
  const busy = useRef(false);
  const invoiceAbort = useRef<AbortController|null>(null);

  useEffect(() => {
    const current = ++generation.current;
    const controller = new AbortController();
    invoiceAbort.current?.abort();
    setShown(null); setMessage(''); setPending(enabled); busy.current = enabled;
    if (enabled) {
      loadStripeTestCheckouts(cursor ?? undefined,controller.signal).then(page => {
        if (generation.current === current) {setShown({scope,page});setMessage('');}
      }).catch(() => {
        if (generation.current === current) setMessage('Current TEST records are unavailable. Reload from the first page.');
      }).finally(() => {
        if (generation.current === current) {setPending(false);busy.current = false;}
      });
    }
    return () => {generation.current++;controller.abort();invoiceAbort.current?.abort();};
  },[enabled,scope,cursor,reload]);

  if (!enabled) return null;
  const page = shown?.scope === scope ? shown.page : null;
  const reloadFirst = () => {if (busy.current) return;setCursor(null);setReload(value=>value+1);};
  const nextPage = () => {if (busy.current || !page?.next_order_id) return;setCursor(page.next_order_id);};
  async function refreshInvoice(orderId:string,intentId:string) {
    if (busy.current || !page) return;
    busy.current = true; setPending(true); setShown(null); setMessage('');
    const current = ++generation.current;
    const controller = new AbortController(); invoiceAbort.current = controller;
    try {
      const invoice = await loadStripeTestInvoice(orderId,intentId,controller.signal);
      if (generation.current !== current) return;
      const updated = page.checkouts.map(order => order.order_id === orderId
        ? {...order,invoices:order.invoices.map(item => item.intent_id === intentId ? invoice : item)} : order);
      setShown({scope,page:{...page,checkouts:updated}});
      setMessage('Local TEST invoice refreshed. A browser return is not payment confirmation.');
    } catch {
      if (generation.current === current) setMessage('Current TEST records are unavailable. Reload from the first page.');
    } finally {
      if (generation.current === current) {invoiceAbort.current = null;busy.current = false;setPending(false);}
    }
  }

  return <section className="panel" aria-labelledby="stripe-test-invoices-title">
    <p className="eyebrow">STRIPE TEST · READ-ONLY</p>
    <h2 id="stripe-test-invoices-title">TEST checkout status and invoices</h2>
    <p>These are local TEST records for this signed-in customer. Returning from a checkout page or seeing a receipt does not confirm payment; only the reconciled server invoice can show settlement. No live charge is available here.</p>
    <p className="notice" role="status" aria-live="polite">{message || (pending ? 'Loading local TEST records…' : page?.checkouts.length === 0 ? 'No local TEST checkout records are available.' : '')}</p>
    <button type="button" disabled={pending} onClick={reloadFirst}>Reload TEST records from first page</button>
    {page && <>
      <p>Showing up to ten orders on this page. Later pages are loaded only when you request them.</p>
      <ol className="intake-list">{page.checkouts.map(order => <li className="intake-card" key={order.order_id}>
        <h3>Order {order.order_id}</h3>
        <p>Order state: {order.order_state}. Version {order.order_version}.</p>
        {order.quotes.length > 0 && <ul>{order.quotes.map(quote => <li key={quote.quote_id}>TEST quote {money(quote.amount_minor)} USD · {quote.state} · version {quote.quote_version}</li>)}</ul>}
        {order.quotes.length === 0 && <p>No TEST quote is recorded for this order.</p>}
        {order.quote_history_truncated && <p>Quote history is truncated to the latest 25 records. This is not the complete history.</p>}
        {order.invoices.length > 0 && <ul>{order.invoices.map(invoice => <li key={invoice.intent_id}>
          <p>TEST invoice {money(invoice.amount_minor)} USD · {stateText[invoice.state]}.</p>
          {invoice.refunded_minor > 0 && <p>Recorded successful TEST refunds: {money(invoice.refunded_minor)} USD.</p>}
          {invoice.reconciliation_required && <p>Reconciliation is required; do not treat this as final settlement.</p>}
          <button type="button" disabled={pending} onClick={()=>void refreshInvoice(order.order_id,invoice.intent_id)}>Reload this TEST invoice</button>
        </li>)}</ul>}
        {order.invoices.length === 0 && <p>No TEST invoice is recorded for this order.</p>}
        {order.intent_history_truncated && <p>Invoice history is truncated to the latest 25 records. This is not the complete history.</p>}
      </li>)}</ol>
      {page.next_order_id && <button type="button" disabled={pending} onClick={nextPage}>Load next 10 TEST orders</button>}
      {cursor && <button type="button" disabled={pending} onClick={reloadFirst}>Back to first TEST page</button>}
    </>}
  </section>;
}
